import React, {useState} from 'react';
import {Formik, Field} from 'formik';
import {withStyles} from '@material-ui/core/styles';
import styled from 'styled-components';
import TextField from '@material-ui/core/TextField';
import FormGrid from '../../../components/Form/FormGrid';
import Switch from '../../../components/Form/Switch';
import {PrimaryButton, ButtonRow} from '../../../components/Buttons';
import SingleDatePicker from '../../../components/SingleDatePicker';
import moment from 'moment';
import {InputAdornment} from '@material-ui/core';
import appConfig from '../../../../app-config';

const SwitchWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const Button = styled(PrimaryButton)`
  min-width: 0px;
  width: 175px;
`;

const PhoneNumberText = styled.div`
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }
`;

const MaskIcon = styled.i`
  cursor: pointer;
  color: #0d0e12;
`;

const styles = theme => ({
  input: {
    fontFamily: theme.typography.fontFamily.main,
  },
});

const RegistrationForm = ({
  createAccount,
  checkedEmail,
  isRequesting,
  resetCheckedEmail,
  classes,
  isBirthdayEnabled,
  showReferralCode,
}) => {
  const [passwordMaskState, setPasswordMaskState] = useState(true);
  const [passwordConfirmMaskState, setPasswordConfirmMaskState] = useState(
    true
  );
  const [error, setError] = useState(null);
  const marketing =
    appConfig.appSource === 'madradish_catering' ||
    appConfig.appSource === 'madradish'
      ? true
      : false;

  return (
    <Formik
      initialValues={{marketing: marketing}}
      validate={values => {
        let errors = {};
        if (!values.password) {
          errors.password = 'Required';
        }
        if (!values.confirm_password) {
          errors.confirm_password = 'Required';
        }
        if (values.password.length < 8) {
          errors.password = 'Password must be a minimum of 8 characters';
        }
        if (!new RegExp('^(?=.*[A-Z])^(?=.*[a-z])').test(values.password)) {
          errors.password =
            'Password must contain at least one uppercase and one lowercase';
        }
        if (!new RegExp('^(?=.*[^a-zA-Z])').test(values.password)) {
          errors.password =
            'Password must contain at least one digit or symbol';
        }
        if (values.password !== values.confirm_password) {
          errors.confirm_password = 'Password mismatch';
        }
        if (!values.first_name) {
          errors.first_name = 'Required';
        }
        if (!values.last_name) {
          errors.last_name = 'Required';
        }
        if (!values.phone_number) {
          errors.phone_number = 'Required';
        } else if (JSON.stringify(values.phone_number).length > 10) {
          errors.phone_number = 'No more than 10 digits allowed';
        }
        return errors;
      }}
      onSubmit={values => {
        if (error) return;
        createAccount({
          first_name: values.first_name,
          last_name: values.last_name,
          password: values.password,
          phone_number: values.phone_number,
          marketing: values.marketing,
          birthdate: values.birthdate
            ? moment(values.birthdate).format('YYYY-MM-DD')
            : '',
          email: checkedEmail,
          referral_code: values.referral_code,
        });
      }}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        isValidating,
      }) => (
        <form onSubmit={handleSubmit}>
          <FormGrid>
            <TextField
              id="RegistrationForm-FirstNameField"
              label="First Name"
              name="first_name"
              type="text"
              autoComplete="given-name"
              value={values.first_name}
              onChange={handleChange}
              required
              onBlur={handleBlur}
              error={touched.first_name && errors.first_name ? true : false}
              helperText={touched.first_name && errors.first_name}
              InputLabelProps={{
                className: classes.input,
                required: false,
              }}
            />
            <TextField
              id="RegistrationForm-LastNameField"
              label="Last Name"
              name="last_name"
              type="text"
              autoComplete="family-name"
              value={values.last_name}
              onChange={handleChange}
              required
              onBlur={handleBlur}
              error={touched.last_name && errors.last_name ? true : false}
              helperText={touched.last_name && errors.last_name}
              InputLabelProps={{
                className: classes.input,
                required: false,
              }}
            />

            <TextField
              id="RegistrationForm-PasswordField"
              label="Password"
              name="password"
              type={passwordMaskState ? 'password' : 'text'}
              autocomplete="new-password"
              value={values.password}
              onChange={handleChange}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <MaskIcon
                      className={
                        passwordMaskState ? 'fas fa-eye-slash' : 'fas fa-eye'
                      }
                      onClick={() => setPasswordMaskState(!passwordMaskState)}
                    />
                  </InputAdornment>
                ),
              }}
              onBlur={handleBlur}
              error={touched.password && errors.password ? true : false}
              helperText={touched.password && errors.password}
              InputLabelProps={{
                className: classes.input,
                required: false,
              }}
            />
            <TextField
              id="RegistrationForm-ConfirmPasswordField"
              label="Confirm Password"
              name="confirm_password"
              type={passwordConfirmMaskState ? 'password' : 'text'}
              autocomplete="new-password"
              value={values.confirm_password}
              onChange={handleChange}
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <MaskIcon
                      className={
                        passwordConfirmMaskState
                          ? 'fas fa-eye-slash'
                          : 'fas fa-eye'
                      }
                      onClick={() =>
                        setPasswordConfirmMaskState(!passwordConfirmMaskState)
                      }
                    />
                  </InputAdornment>
                ),
              }}
              onBlur={handleBlur}
              error={
                touched.confirm_password && errors.confirm_password
                  ? true
                  : false
              }
              helperText={touched.confirm_password && errors.confirm_password}
              InputLabelProps={{
                className: classes.input,
                required: false,
              }}
            />

            {isBirthdayEnabled ? (
              <Field
                id="RegistrationForm-BirthdateField"
                name="birthdate"
                component={SingleDatePicker}
                label="Birthday - Optional"
                clearable
                disableFuture
                openTo="year"
                format={'dd/MM/yyyy'}
                views={['year', 'month', 'date']}
                InputLabelProps={{
                  className: classes.input,
                }}
                onChange={handleChange}
                value={values.birthdate}
              />
            ) : null}

            <PhoneNumberText>
              <TextField
                id="RegistrationForm-PhoneNumberField"
                label="Phone Number"
                name="phone_number"
                type="number"
                autoComplete="number"
                value={values.phone_number}
                onKeyDown={evt =>
                  (evt.key === 'e' || evt.key === '-') && evt.preventDefault()
                }
                onChange={handleChange}
                required
                onBlur={handleBlur}
                error={
                  touched.phone_number && errors.phone_number ? true : false
                }
                helperText={touched.phone_number && errors.phone_number}
                style={{width: '100%'}}
                InputLabelProps={{
                  className: classes.input,
                  required: false,
                }}
              />
            </PhoneNumberText>
            {showReferralCode ? (
              <TextField
                id="RegistrationForm-ReferralCode"
                label="Referral Code - Optional"
                name="referral_code"
                type="text"
                value={values.referral_code}
                onChange={handleChange}
                onBlur={handleBlur}
                InputLabelProps={{
                  className: classes.input,
                  required: false,
                }}
              />
            ) : null}
            <SwitchWrapper>
              <Switch
                name="marketing"
                label={
                  appConfig.appSource === 'iQ'
                    ? 'Email communication'
                    : 'Email me about deals and offers'
                }
                checked={values.marketing}
                onChange={handleChange}
              />
            </SwitchWrapper>
            <ButtonRow justifyContent="space-between">
              <Button
                id="RegistrationForm-BackButton"
                disabled={isRequesting}
                type="button"
                onClick={resetCheckedEmail}
              >
                Back
              </Button>
              <Button
                id="RegistrationForm-SubmitButton"
                disabled={isRequesting}
                type="submit"
              >
                Proceed
              </Button>
            </ButtonRow>
          </FormGrid>
        </form>
      )}
    </Formik>
  );
};

export default withStyles(styles)(RegistrationForm);
